import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { buildSkeletonPattern, MIN_CELL_WIDTH } from '@components/VirtualMasonry/masonryConstants';
import {
  VirtualMasonry,
  VirtualMasonryCellProps,
  VirtualMasonryRef,
} from '@components/VirtualMasonry/VirtualMasonry';
import { useElementSize } from '@lib/hooks/sizing/element-size';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { MomentMergeProvider } from '@stores/MomentMerge/MomentMergeStore';
import {
  encodeMomentToString,
  MomentIdentifierString,
} from '@stores/MomentSelection/MomentSelection';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { VirtualMasonryGridCellSkeleton } from '../../../../components/VirtualMasonry/VirtualMasonryGridCellSkeleton/VirtualMasonryGridCellSkeleton';
import { MomentModalProvider, useMomentModal } from '../../context/MomentModalContext';
import { TranscriptOnlyFootnote } from '../../SearchPage/components/TranscriptOnlyFootnote/TranscriptOnlyFootnote';
import { CenteredCardProvider } from './CenteredCardContext';
import { Moment } from './components/Moment/Moment';
import { MomentModal } from './components/MomentModal/MomentModal';
import { MomentSelectionBar } from './components/MomentSelection/MomentSelectionBar';
import css from './MomentGrid.module.scss';

const MomentGridContent = observer(function MomentGridContent() {
  const [containerRef, containerWidth, containerHeight] = useElementSize<HTMLDivElement>();
  const masonryRef = useRef<VirtualMasonryRef>(null);
  // Ref to access the scrollable content inside GradientScrollDiv
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const libraryManager = useLibraryManager();
  const librarySearch = libraryManager.multiLibSearch;
  const resultsList = librarySearch.results?.results ?? [];
  const resultsMedia = librarySearch.results?.medias ?? {};

  const isUpdating = librarySearch.anyLibrarySearching;

  useEffect(() => {
    masonryRef.current?.scrollToTop();
  }, [libraryManager.multiLibSearch.request?.text]);

  const cells = isUpdating
    ? buildSkeletonPattern(containerWidth, window.innerHeight, MIN_CELL_WIDTH, 16).map(
        (
          cardShape,
          index,
        ): {
          key: string;
          cardShape: 'tall' | 'short';
          children: React.ReactNode;
        } => ({
          key: `skeleton-${index}`,
          cardShape,
          children: <VirtualMasonryGridCellSkeleton />,
        }),
      )
    : resultsList.map((result) => {
        const media = resultsMedia[result.mediaId];
        const w = media?.metadata?.video?.width || 1;
        const h = media?.metadata?.video?.height || 1;
        const ratio = w / h;
        const cardShape: 'tall' | 'short' =
          ratio >= 0.85 && ratio <= 1.15 ? 'short' : h > w ? 'tall' : 'short';

        const children = ({
          isVisible,
          columnIndex,
          rowIndex,
          width,
          height,
        }: VirtualMasonryCellProps) => (
          <Moment
            result={result}
            media={media}
            isVisible={isVisible}
            columnIndex={columnIndex}
            rowIndex={rowIndex}
            width={width}
            height={height}
          />
        );

        const key = encodeMomentToString(
          result.libraryId,
          result.mediaId,
          result.startTime,
          result.endTime,
        ) as MomentIdentifierString;

        return {
          key,
          cardShape,
          children,
        };
      });

  return (
    <GradientScrollDiv className={css.resultGridContainer} ref={scrollContainerRef}>
      <div ref={containerRef}>
        <VirtualMasonry
          ref={masonryRef}
          minCellWidth={MIN_CELL_WIDTH}
          width={containerWidth}
          cells={cells}
          masonryCellGap={16}
          sidesMargin={8}
          addBorder={false}
          overscanWeight={1.0}
          throttleTime={100}
          paddingBottom={200}
          className={!isUpdating ? css.masonry : ''}
          scrollContainerRef={scrollContainerRef}
          footer={<TranscriptOnlyFootnote />}
        />
      </div>
    </GradientScrollDiv>
  );
});

const MomentModalWrapper = observer(function MomentModalWrapper() {
  const { currentResult, currentMedia, isOpen, closeModal } = useMomentModal();

  if (!isOpen || !currentResult || !currentMedia) return null;

  return (
    <MomentModal
      variant="search-result"
      surface="search"
      result={currentResult}
      media={currentMedia}
      open={isOpen}
      onClose={closeModal}
    />
  );
});

export const MomentGrid = observer(function MomentGrid() {
  const libraryManager = useLibraryManager();
  const isSelectingMoments = libraryManager.multiLibSearch.momentSelection.isSelecting;

  return (
    <CenteredCardProvider>
      <MomentModalProvider>
        <MomentMergeProvider arguments={[libraryManager.renderer]}>
          <MomentGridContent />
          {isSelectingMoments && <MomentSelectionBar />}
          <MomentModalWrapper />
        </MomentMergeProvider>
      </MomentModalProvider>
    </CenteredCardProvider>
  );
});
